import type {
  Bounding,
  Chart,
  Coordinate,
  Overlay,
  OverlayEvent,
  OverlayFigure,
  OverlayPerformEventParams,
  OverlayTemplate,
  Point,
} from "klinecharts";

export const RISK_REWARD_OVERLAY_NAME = "riskReward";

const DEFAULT_TARGET_RATIO = 2;
const STATS_FONT_SIZE_PX = 11;
const STATS_FONT_WEIGHT = 600;
const STATS_FONT_FAMILY =
  '"Geist Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace';
const STATS_BOX_GAP_PX = 10;
const STATS_BADGE_HEIGHT_PX = 14;
const MIN_LINE_WIDTH_PX = 24;
const EDGE_PADDING_PX = 8;
const PRICE_PILL_HEIGHT_PX = 16;
const PRICE_PILL_HORIZONTAL_PADDING_PX = 6;
const PRICE_PILL_MARGIN_PX = 8;

let registered = false;
let measureContext: CanvasRenderingContext2D | null = null;
let cssColorContext: CanvasRenderingContext2D | null = null;

type RiskRewardOverlayState = Overlay<unknown> & {
  _riskRewardDragging?: boolean;
  _riskRewardHovered?: boolean;
  _riskRewardSelected?: boolean;
  _riskRewardPrevPressedPoints?: Array<Partial<Point>> | null;
  _prevPressedPoints?: Array<Partial<Point>> | null;
};

interface Palette {
  entryLine: string;
  entryLineGhost: string;
  profitFill: string;
  profitLine: string;
  lossFill: string;
  lossLine: string;
  textPrimary: string;
  textSecondary: string;
  textInverse: string;
  border: string;
  surface: string;
  surfaceElevated: string;
  ratioBadge: string;
  profitProgressFill: string;
  lossProgressFill: string;
  pointBorderColor: string;
  activeBorderColor: string;
  activeColor: string;
}

function getMeasureContext() {
  if (measureContext) return measureContext;
  if (typeof document === "undefined") return null;

  const canvas = document.createElement("canvas");
  measureContext = canvas.getContext("2d");
  return measureContext;
}

function getCssColorContext() {
  if (cssColorContext) return cssColorContext;
  if (typeof document === "undefined") return null;

  const canvas = document.createElement("canvas");
  cssColorContext = canvas.getContext("2d");
  return cssColorContext;
}

function getCssToken(token: string): string {
  if (typeof document !== "undefined") {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(token)
      .trim();
    if (value) {
      return value;
    }
  }

  const fallbacks: Record<string, string> = {
    "--accent-primary": "#0EC4B4",
    "--accent-focus": "rgba(14, 196, 180, 0.18)",
    "--state-positive-surface": "rgba(22, 184, 90, 0.12)",
    "--state-positive": "#16B85A",
    "--state-negative-surface": "rgba(240, 69, 69, 0.12)",
    "--state-negative": "#F04545",
    "--chart-overlay-text": "#E4EAF4",
    "--text-secondary": "#8FA8C8",
    "--surface-canvas": "#080D17",
    "--border-strong": "rgba(255,255,255,0.18)",
    "--chart-overlay-surface": "rgba(8, 13, 23, 0.96)",
    "--surface-chrome-raised": "rgba(18, 26, 43, 0.96)",
    "--bullish": "#16B85A",
    "--bearish": "#F04545",
    "--bg-surface": "#0C1320",
    "--bg-overlay": "#182235",
    "--text-primary": "#E4EAF4",
  };

  return fallbacks[token] ?? "#E4EAF4";
}

function measureTextWidth(text: string | number | null | undefined) {
  const context = getMeasureContext();
  if (!context) {
    return String(text ?? "").length * 7;
  }

  context.font = `${STATS_FONT_WEIGHT} ${STATS_FONT_SIZE_PX}px ${STATS_FONT_FAMILY}`;
  return Math.ceil(context.measureText(String(text ?? "")).width);
}

function toFiniteNumber(value: unknown): number | null {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : null;
}

function clamp(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

function clonePoint(point: Partial<Point> = {}) {
  return {
    timestamp: toFiniteNumber(point.timestamp) ?? undefined,
    dataIndex: toFiniteNumber(point.dataIndex) ?? undefined,
    value: toFiniteNumber(point.value) ?? undefined,
  };
}

function clonePoints(points: Array<Partial<Point>> = []) {
  return Array.isArray(points) ? points.map((point) => clonePoint(point)) : [];
}

function copyPointCoordinate(target?: Partial<Point>, source?: Partial<Point>) {
  if (!target || !source) return;

  const nextTimestamp = toFiniteNumber(source.timestamp);
  const nextDataIndex = toFiniteNumber(source.dataIndex);
  if (nextTimestamp != null) {
    target.timestamp = nextTimestamp;
  }
  if (nextDataIndex != null) {
    target.dataIndex = nextDataIndex;
  }
}

function resolveRightBoundary(points: Array<Partial<Point>> = []) {
  const point = points[1] ?? points[2] ?? null;
  return point ? clonePoint(point) : {};
}

function offsetPointFromPrevious(
  previousPoint: Partial<Point> | undefined,
  deltaDataIndex: number | null,
  deltaTimestamp: number | null,
  deltaValue: number | null,
) {
  const point = clonePoint(previousPoint);
  const previousDataIndex = toFiniteNumber(previousPoint?.dataIndex);
  const previousTimestamp = toFiniteNumber(previousPoint?.timestamp);
  const previousValue = toFiniteNumber(previousPoint?.value);

  if (previousDataIndex != null && deltaDataIndex != null) {
    point.dataIndex = previousDataIndex + deltaDataIndex;
  }
  if (previousTimestamp != null && deltaTimestamp != null) {
    point.timestamp = previousTimestamp + deltaTimestamp;
  }
  if (previousValue != null && deltaValue != null) {
    point.value = previousValue + deltaValue;
  }

  return point;
}

function formatCompactNumber(value: unknown, maximumFractionDigits = 2) {
  const finiteValue = toFiniteNumber(value);
  if (finiteValue == null) return "--";

  const formatted = finiteValue.toFixed(maximumFractionDigits);
  return formatted.replace(/\.?0+$/, "");
}

function formatPrice(value: unknown, precision = 2) {
  const finiteValue = toFiniteNumber(value);
  if (finiteValue == null) return "--";
  return finiteValue.toFixed(Math.max(0, precision));
}

function formatRatio(ratio: unknown) {
  const finiteRatio = toFiniteNumber(ratio);
  if (finiteRatio == null || finiteRatio <= 0) return "1:0";
  return `1:${formatCompactNumber(finiteRatio, 2)}`;
}

function getTextStyles(
  color: string,
  options: { size?: number; weight?: number } = {},
) {
  const { size = STATS_FONT_SIZE_PX, weight = STATS_FONT_WEIGHT } = options;
  return {
    style: "fill",
    color,
    size,
    family: STATS_FONT_FAMILY,
    weight,
    backgroundColor: "transparent",
    borderColor: "transparent",
    borderSize: 0,
    borderStyle: "solid",
    borderRadius: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  };
}

function withAlpha(color: string, alpha: number) {
  const context = getCssColorContext();
  if (!context) return color;

  context.fillStyle = "#000000";
  context.fillStyle = color;
  const normalized = context.fillStyle;
  const match = normalized.match(/^rgba?\(([^)]+)\)$/i);
  if (!match) return color;

  const channels = match[1]
    .split(",")
    .map((part) => Number.parseFloat(part.trim()));
  if (
    channels.length < 3 ||
    channels.slice(0, 3).some((value) => !Number.isFinite(value))
  ) {
    return color;
  }

  return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${alpha})`;
}

function getDefaultPalette(): Omit<
  Palette,
  "pointBorderColor" | "activeBorderColor" | "activeColor"
> {
  return {
    entryLine: getCssToken("--accent-primary"),
    entryLineGhost: getCssToken("--accent-focus"),
    profitFill: getCssToken("--state-positive-surface"),
    profitLine: getCssToken("--state-positive"),
    lossFill: getCssToken("--state-negative-surface"),
    lossLine: getCssToken("--state-negative"),
    textPrimary: getCssToken("--chart-overlay-text"),
    textSecondary: getCssToken("--text-secondary"),
    textInverse: getCssToken("--surface-canvas"),
    border: getCssToken("--border-strong"),
    surface: getCssToken("--chart-overlay-surface"),
    surfaceElevated: getCssToken("--surface-chrome-raised"),
    ratioBadge: withAlpha(getCssToken("--accent-primary"), 0.18),
    profitProgressFill: withAlpha(getCssToken("--state-positive"), 0.2),
    lossProgressFill: withAlpha(getCssToken("--state-negative"), 0.2),
  };
}

function getPalette(overlay: RiskRewardOverlayState): Palette {
  const defaults = getDefaultPalette();
  const pointStyles = overlay.styles?.point as Record<string, unknown> | undefined;
  const pointBorderColor = pointStyles?.borderColor ?? defaults.entryLine;
  const activeBorderColor = pointStyles?.activeBorderColor ?? pointBorderColor;
  const activeColor = pointStyles?.activeColor ?? defaults.entryLineGhost;

  return {
    ...defaults,
    pointBorderColor: String(pointBorderColor),
    activeBorderColor: String(activeBorderColor),
    activeColor: String(activeColor),
  };
}

export function getRiskRewardDirection(
  entryValue: unknown,
  comparisonValue: unknown,
  fallback: "long" | "short" | null = "long",
) {
  const entry = toFiniteNumber(entryValue);
  const comparison = toFiniteNumber(comparisonValue);
  if (entry == null || comparison == null) return fallback;
  if (comparison < entry) return "long";
  if (comparison > entry) return "short";
  return fallback;
}

function getFallbackDirection(
  entryValue: unknown,
  stopValue: unknown,
  targetValue: unknown,
) {
  const stopDirection = getRiskRewardDirection(entryValue, stopValue, null);
  if (stopDirection) return stopDirection;
  return getRiskRewardDirection(entryValue, targetValue, "long") ?? "long";
}

export function buildRiskRewardTargetValue(
  entryValue: unknown,
  stopValue: unknown,
  ratio = DEFAULT_TARGET_RATIO,
) {
  const entry = toFiniteNumber(entryValue);
  const stop = toFiniteNumber(stopValue);
  const targetRatio = Math.max(0, toFiniteNumber(ratio) ?? DEFAULT_TARGET_RATIO);

  if (entry == null || stop == null) {
    return entry ?? stop ?? 0;
  }

  const risk = Math.abs(entry - stop);
  if (risk === 0) return entry;

  const direction = getRiskRewardDirection(entry, stop, null);
  if (direction === "long") {
    return entry + risk * targetRatio;
  }
  if (direction === "short") {
    return entry - risk * targetRatio;
  }
  return entry;
}

export function buildRiskRewardStats(
  entryValue: unknown,
  stopValue: unknown,
  targetValue: unknown,
) {
  const direction = getFallbackDirection(entryValue, stopValue, targetValue);
  const stopDistance = Math.abs(
    (toFiniteNumber(entryValue) ?? 0) - (toFiniteNumber(stopValue) ?? 0),
  );
  const targetDistance = Math.abs(
    (toFiniteNumber(targetValue) ?? 0) - (toFiniteNumber(entryValue) ?? 0),
  );
  const ratio = stopDistance > 0 ? targetDistance / stopDistance : 0;

  return {
    direction,
    directionLabel: direction === "short" ? "Short" : "Long",
    stopDistance,
    targetDistance,
    ratio,
    stopDistanceLabel: formatCompactNumber(stopDistance, 2),
    targetDistanceLabel: formatCompactNumber(targetDistance, 2),
    ratioLabel: formatRatio(ratio),
  };
}

function getSign(value: unknown) {
  const finiteValue = toFiniteNumber(value);
  if (finiteValue == null || finiteValue === 0) return 0;
  return finiteValue > 0 ? 1 : -1;
}

function buildStopValueFromTarget(
  entryValue: unknown,
  targetValue: unknown,
  ratio = DEFAULT_TARGET_RATIO,
) {
  const entry = toFiniteNumber(entryValue);
  const target = toFiniteNumber(targetValue);
  const targetRatio = Math.max(0.0001, toFiniteNumber(ratio) ?? DEFAULT_TARGET_RATIO);
  if (entry == null || target == null) return entry ?? target ?? 0;

  const reward = Math.abs(target - entry);
  if (reward === 0) return entry;

  const risk = reward / targetRatio;
  const direction = target > entry ? "long" : target < entry ? "short" : null;
  if (direction === "long") {
    return entry - risk;
  }
  if (direction === "short") {
    return entry + risk;
  }
  return entry;
}

function shouldRebuildTargetFromStop(
  entryValue: unknown,
  stopValue: unknown,
  targetValue: unknown,
) {
  const stopSide = getSign((toFiniteNumber(stopValue) ?? 0) - (toFiniteNumber(entryValue) ?? 0));
  const targetSide = getSign(
    (toFiniteNumber(targetValue) ?? 0) - (toFiniteNumber(entryValue) ?? 0),
  );

  return stopSide !== 0 && (targetSide === 0 || targetSide === stopSide);
}

function shouldRebuildStopFromTarget(
  entryValue: unknown,
  stopValue: unknown,
  targetValue: unknown,
) {
  const stopSide = getSign((toFiniteNumber(stopValue) ?? 0) - (toFiniteNumber(entryValue) ?? 0));
  const targetSide = getSign(
    (toFiniteNumber(targetValue) ?? 0) - (toFiniteNumber(entryValue) ?? 0),
  );

  return targetSide !== 0 && (stopSide === 0 || stopSide === targetSide);
}

function buildStatsFigures(
  leftX: number,
  rightX: number,
  topY: number,
  bottomY: number,
  stats: ReturnType<typeof buildRiskRewardStats>,
  palette: Palette,
  bounding: Bounding,
): OverlayFigure[] {
  const ratioBadgeText = `RR ${formatCompactNumber(stats.ratio, 2)}`;
  const ratioBadgeWidth = measureTextWidth(ratioBadgeText) + 12;
  const overallTop = Math.min(topY, bottomY);
  const overallBottom = Math.max(topY, bottomY);
  const chartWidth = Math.max(0, Number(bounding?.width) || 0);
  const preferredOutsideX = rightX + STATS_BOX_GAP_PX;
  const pillWidth = ratioBadgeWidth;
  const pillHeight = STATS_BADGE_HEIGHT_PX;
  const maxCardX = Math.max(EDGE_PADDING_PX, chartWidth - pillWidth - EDGE_PADDING_PX);
  const canPlaceOutside = preferredOutsideX <= maxCardX;
  const minInsideX = Math.max(EDGE_PADDING_PX, leftX + EDGE_PADDING_PX);
  const insidePreferredX = Math.max(minInsideX, rightX - pillWidth - EDGE_PADDING_PX);
  const boxX = canPlaceOutside
    ? preferredOutsideX
    : clamp(insidePreferredX, minInsideX, Math.max(minInsideX, maxCardX));
  const maxBoxY = Math.max(EDGE_PADDING_PX, overallBottom - pillHeight - EDGE_PADDING_PX);
  const boxY = clamp(
    (overallTop + overallBottom) / 2 - pillHeight / 2,
    EDGE_PADDING_PX,
    maxBoxY,
  );

  return [
    {
      type: "rect",
      attrs: {
        x: boxX,
        y: boxY,
        width: pillWidth,
        height: pillHeight,
      },
      styles: {
        style: "stroke_fill",
        color: palette.ratioBadge,
        borderColor: palette.entryLine,
        borderSize: 1,
        borderStyle: "solid",
        borderRadius: 4,
      },
      ignoreEvent: true,
    },
    {
      type: "text",
      attrs: {
        x: boxX + pillWidth / 2,
        y: boxY + pillHeight / 2,
        text: ratioBadgeText,
        align: "center",
        baseline: "middle",
      },
      styles: getTextStyles(palette.entryLine, {
        size: 10,
        weight: 600,
      }),
      ignoreEvent: true,
    },
  ];
}

function buildPricePillFigures({
  x,
  y,
  text,
  align = "center",
  backgroundColor,
  borderColor,
  textColor,
  bounding,
}: {
  x: number;
  y: number;
  text: string;
  align?: "center" | "left" | "right";
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  bounding: Bounding;
}): OverlayFigure[] {
  if (!Number.isFinite(x) || !Number.isFinite(y) || !text) {
    return [];
  }

  const textWidth = measureTextWidth(text);
  const pillWidth = textWidth + PRICE_PILL_HORIZONTAL_PADDING_PX * 2;
  const chartWidth = Math.max(0, Number(bounding?.width) || 0);
  let leftX = x - pillWidth / 2;

  if (align === "left") {
    leftX = x;
  } else if (align === "right") {
    leftX = x - pillWidth;
  }

  leftX = clamp(
    leftX,
    EDGE_PADDING_PX,
    Math.max(EDGE_PADDING_PX, chartWidth - pillWidth - EDGE_PADDING_PX),
  );

  return [
    {
      type: "rect",
      attrs: {
        x: leftX,
        y: y - PRICE_PILL_HEIGHT_PX / 2,
        width: pillWidth,
        height: PRICE_PILL_HEIGHT_PX,
      },
      styles: {
        style: "stroke_fill",
        color: backgroundColor,
        borderColor,
        borderSize: 1,
        borderStyle: "solid",
        borderRadius: 3,
      },
      ignoreEvent: true,
    },
    {
      type: "text",
      attrs: {
        x: leftX + pillWidth / 2,
        y,
        text,
        align: "center",
        baseline: "middle",
      },
      styles: getTextStyles(textColor, {
        size: 10,
        weight: 600,
      }),
      ignoreEvent: true,
    },
  ];
}

function buildInlinePriceFigures({
  chart,
  overlay,
  entryY,
  stopY,
  targetY,
  leftX,
  rightX,
  bounding,
  palette,
}: {
  chart: Chart;
  overlay: RiskRewardOverlayState;
  entryY: number;
  stopY: number;
  targetY: number;
  leftX: number;
  rightX: number;
  bounding: Bounding;
  palette: Palette;
}): OverlayFigure[] {
  const shouldShow = Boolean(overlay._riskRewardHovered || overlay._riskRewardSelected);
  if (!shouldShow) {
    return [];
  }

  const precision = Math.max(0, Number(chart.getSymbol?.()?.pricePrecision) || 2);
  const entryValue = formatPrice(overlay.points[0]?.value, precision);
  const stopValue = formatPrice(overlay.points[1]?.value, precision);
  const targetValue = formatPrice(overlay.points[2]?.value, precision);
  const direction = getFallbackDirection(
    overlay.points[0]?.value,
    overlay.points[1]?.value,
    overlay.points[2]?.value,
  );
  const midX = leftX + (rightX - leftX) / 2;
  const targetLabelY =
    direction === "long"
      ? Math.min(entryY, targetY) + PRICE_PILL_MARGIN_PX + PRICE_PILL_HEIGHT_PX / 2
      : Math.max(entryY, targetY) - PRICE_PILL_MARGIN_PX - PRICE_PILL_HEIGHT_PX / 2;
  const stopLabelY =
    direction === "long"
      ? Math.max(stopY, entryY) + PRICE_PILL_MARGIN_PX + PRICE_PILL_HEIGHT_PX / 2
      : Math.min(stopY, entryY) - PRICE_PILL_MARGIN_PX - PRICE_PILL_HEIGHT_PX / 2;

  return [
    ...buildPricePillFigures({
      x: midX,
      y: targetLabelY,
      text: targetValue,
      backgroundColor: palette.surface,
      borderColor: palette.profitLine,
      textColor: palette.textPrimary,
      bounding,
    }),
    ...buildPricePillFigures({
      x: midX,
      y: stopLabelY,
      text: stopValue,
      backgroundColor: palette.surface,
      borderColor: palette.lossLine,
      textColor: palette.textPrimary,
      bounding,
    }),
    ...(overlay._riskRewardDragging
      ? buildPricePillFigures({
          x: midX,
          y: entryY,
          text: entryValue,
          backgroundColor: palette.surface,
          borderColor: palette.entryLine,
          textColor: palette.textPrimary,
          bounding,
        })
      : []),
  ];
}

function buildProgressFigure({
  leftX,
  rightX,
  entryY,
  touchedY,
  fillColor,
}: {
  leftX: number;
  rightX: number;
  entryY: number;
  touchedY: number | null;
  fillColor: string;
}) {
  if (!Number.isFinite(touchedY) || touchedY === entryY) return null;
  const resolvedTouchedY = Number(touchedY);

  return {
    type: "rect",
    attrs: {
      x: leftX,
      y: Math.min(entryY, resolvedTouchedY),
      width: Math.max(1, rightX - leftX),
      height: Math.max(1, Math.abs(entryY - resolvedTouchedY)),
    },
    styles: {
      style: "fill",
      color: fillColor,
      borderColor: "transparent",
      borderSize: 0,
      borderStyle: "solid",
      borderRadius: 0,
    },
    ignoreEvent: true,
  } satisfies OverlayFigure;
}

function getProgressFigures({
  chart,
  overlay,
  leftX,
  rightX,
  entryY,
  palette,
}: {
  chart: Chart;
  overlay: RiskRewardOverlayState;
  leftX: number;
  rightX: number;
  entryY: number;
  palette: Palette;
}) {
  const candles = chart.getDataList?.();
  const latestCandle =
    Array.isArray(candles) && candles.length > 0 ? candles[candles.length - 1] : null;
  const entry = toFiniteNumber(overlay.points[0]?.value);
  const stop = toFiniteNumber(overlay.points[1]?.value);
  const target = toFiniteNumber(overlay.points[2]?.value);
  const high = toFiniteNumber(latestCandle?.high);
  const low = toFiniteNumber(latestCandle?.low);

  if (entry == null || stop == null || target == null || high == null || low == null) {
    return [];
  }

  const candleTouchesEntry = low <= entry && high >= entry;
  if (!candleTouchesEntry) {
    return [];
  }

  const direction = getFallbackDirection(entry, stop, target);
  let profitTouchValue: number | null = null;
  let lossTouchValue: number | null = null;

  if (direction === "long") {
    if (high >= entry) {
      profitTouchValue = Math.min(target, high);
    }
    if (low <= entry) {
      lossTouchValue = Math.max(stop, low);
    }
  } else {
    if (low <= entry) {
      profitTouchValue = Math.max(target, low);
    }
    if (high >= entry) {
      lossTouchValue = Math.min(stop, high);
    }
  }

  const progressCoordinatesRaw = chart.convertToPixel?.(
    [
      profitTouchValue != null ? { ...overlay.points[2], value: profitTouchValue } : null,
      lossTouchValue != null ? { ...overlay.points[1], value: lossTouchValue } : null,
    ].filter(Boolean) as Array<Partial<Point>>,
    { paneId: overlay.paneId },
  );

  const coordinates = Array.isArray(progressCoordinatesRaw)
    ? progressCoordinatesRaw
    : [progressCoordinatesRaw];

  const profitY = profitTouchValue != null ? Number(coordinates[0]?.y) : null;
  const lossY =
    lossTouchValue != null
      ? Number(coordinates[profitTouchValue != null ? 1 : 0]?.y)
      : null;

  return [
    buildProgressFigure({
      leftX,
      rightX,
      entryY,
      touchedY: profitY,
      fillColor: palette.profitProgressFill,
    }),
    buildProgressFigure({
      leftX,
      rightX,
      entryY,
      touchedY: lossY,
      fillColor: palette.lossProgressFill,
    }),
  ].filter(Boolean) as OverlayFigure[];
}

export function syncRiskRewardPointsForDrawing(
  points: Array<Partial<Point>>,
  performPoint: Partial<Point> | undefined,
) {
  if (!Array.isArray(points) || points.length < 2) return points;

  const entryPoint = points[0] ?? {};
  const stopPoint = points[1] ?? {};
  copyPointCoordinate(stopPoint, performPoint);
  stopPoint.value =
    toFiniteNumber(performPoint?.value) ??
    toFiniteNumber(stopPoint.value) ??
    toFiniteNumber(entryPoint.value) ??
    0;
  points[1] = stopPoint;

  points[2] = {
    ...resolveRightBoundary(points),
    value: buildRiskRewardTargetValue(entryPoint.value, stopPoint.value),
  };

  return points;
}

export function updateRiskRewardPointsForDrag(
  points: Array<Partial<Point>>,
  movedPointIndex: number,
  previousPoints: Array<Partial<Point>> | null | undefined,
) {
  if (!Array.isArray(points) || points.length < 3) {
    return { points, flipped: false };
  }
  if (!Array.isArray(previousPoints) || previousPoints.length < 3) {
    return { points, flipped: false };
  }
  const previousEntry = previousPoints[0];
  const previousStop = previousPoints[1];
  const previousTarget = previousPoints[2];

  if (movedPointIndex === 0) {
    const nextEntry = points[0];
    const deltaValue =
      (toFiniteNumber(nextEntry?.value) ?? 0) - (toFiniteNumber(previousEntry?.value) ?? 0);
    const deltaDataIndex =
      toFiniteNumber(nextEntry?.dataIndex) != null &&
      toFiniteNumber(previousEntry?.dataIndex) != null
        ? (toFiniteNumber(nextEntry?.dataIndex) ?? 0) -
          (toFiniteNumber(previousEntry?.dataIndex) ?? 0)
        : null;
    const deltaTimestamp =
      toFiniteNumber(nextEntry?.timestamp) != null &&
      toFiniteNumber(previousEntry?.timestamp) != null
        ? (toFiniteNumber(nextEntry?.timestamp) ?? 0) -
          (toFiniteNumber(previousEntry?.timestamp) ?? 0)
        : null;

    points[1] = offsetPointFromPrevious(
      previousStop,
      deltaDataIndex,
      deltaTimestamp,
      deltaValue,
    );
    points[2] = offsetPointFromPrevious(
      previousTarget,
      deltaDataIndex,
      deltaTimestamp,
      deltaValue,
    );
    return { points, flipped: false };
  }

  if (movedPointIndex === 1) {
    copyPointCoordinate(points[2], points[1]);
    if (
      shouldRebuildTargetFromStop(points[0]?.value, points[1]?.value, points[2]?.value)
    ) {
      points[2].value = buildRiskRewardTargetValue(points[0]?.value, points[1]?.value);
      return { points, flipped: true };
    }

    return { points, flipped: false };
  }

  if (movedPointIndex === 2) {
    copyPointCoordinate(points[1], points[2]);
    if (
      shouldRebuildStopFromTarget(points[0]?.value, points[1]?.value, points[2]?.value)
    ) {
      points[1].value = buildStopValueFromTarget(points[0]?.value, points[2]?.value);
      return { points, flipped: true };
    }
  }

  return { points, flipped: false };
}

function createRiskRewardOverlayTemplate(): OverlayTemplate<unknown> {
  return {
    name: RISK_REWARD_OVERLAY_NAME,
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    styles: {
      point: {
        radius: 5,
        activeRadius: 9,
        borderSize: 1.5,
        activeBorderSize: 2,
      },
    },
    createPointFigures: ({
      chart,
      overlay,
      coordinates,
      bounding,
    }: {
      chart: Chart;
      overlay: Overlay<unknown>;
      coordinates: Array<Partial<Coordinate>>;
      bounding: Bounding;
    }) => {
      if (coordinates.length < 2) return [];

      const typedOverlay = overlay as RiskRewardOverlayState;
      const palette = getPalette(typedOverlay);
      const entryPoint = typedOverlay.points[0] ?? {};
      const stopPoint = typedOverlay.points[1] ?? {};
      const targetPoint = typedOverlay.points[2] ?? {
        ...resolveRightBoundary(typedOverlay.points),
        value: buildRiskRewardTargetValue(entryPoint.value, stopPoint.value),
      };

      const entryCoordinate = coordinates[0];
      const stopCoordinate = coordinates[1];
      const targetCoordinate =
        coordinates[2] ??
        chart.convertToPixel?.(targetPoint, { paneId: typedOverlay.paneId }) ??
        null;

      if (
        !Number.isFinite(Number(entryCoordinate?.x)) ||
        !Number.isFinite(Number(entryCoordinate?.y)) ||
        !Number.isFinite(Number(stopCoordinate?.x)) ||
        !Number.isFinite(Number(stopCoordinate?.y))
      ) {
        return [];
      }

      const leftX = clamp(
        Number(entryCoordinate.x),
        EDGE_PADDING_PX,
        Math.max(EDGE_PADDING_PX, Number(bounding?.width) || 0),
      );
      const rightX = clamp(
        Math.max(
          Number(stopCoordinate.x),
          Number(targetCoordinate?.x ?? stopCoordinate.x),
          leftX + MIN_LINE_WIDTH_PX,
        ),
        leftX +
          Math.min(
            MIN_LINE_WIDTH_PX,
            Math.max(8, (Number(bounding?.width) || 0) - leftX),
          ),
        Math.max(
          leftX + MIN_LINE_WIDTH_PX,
          Number(bounding?.width) || leftX + MIN_LINE_WIDTH_PX,
        ),
      );
      const entryY = Number(entryCoordinate.y);
      const stopY = Number(stopCoordinate.y);
      const targetY = Number(targetCoordinate?.y ?? entryY);
      const stats = buildRiskRewardStats(entryPoint.value, stopPoint.value, targetPoint.value);
      const topY = Math.min(stopY, targetY);
      const bottomY = Math.max(stopY, targetY);
      const progressFigures = getProgressFigures({
        chart,
        overlay: typedOverlay,
        leftX,
        rightX,
        entryY,
        palette,
      });
      const inlinePriceFigures = buildInlinePriceFigures({
        chart,
        overlay: typedOverlay,
        entryY,
        stopY,
        targetY,
        leftX,
        rightX,
        bounding,
        palette,
      });

      return [
        {
          type: "rect",
          attrs: {
            x: leftX,
            y: Math.min(entryY, stopY),
            width: Math.max(1, rightX - leftX),
            height: Math.max(1, Math.abs(entryY - stopY)),
          },
          styles: {
            style: "stroke_fill",
            color: palette.lossFill,
            borderColor: palette.lossLine,
            borderSize: 1,
            borderStyle: "solid",
            borderRadius: 0,
          },
        },
        {
          type: "rect",
          attrs: {
            x: leftX,
            y: Math.min(entryY, targetY),
            width: Math.max(1, rightX - leftX),
            height: Math.max(1, Math.abs(entryY - targetY)),
          },
          styles: {
            style: "stroke_fill",
            color: palette.profitFill,
            borderColor: palette.profitLine,
            borderSize: 1,
            borderStyle: "solid",
            borderRadius: 0,
          },
        },
        ...progressFigures,
        {
          type: "line",
          attrs: {
            coordinates: [
              { x: leftX, y: entryY },
              { x: rightX, y: entryY },
            ],
          },
          styles: {
            color: palette.entryLine,
            size: 1.6,
            style: "solid",
          },
        },
        {
          type: "line",
          attrs: {
            coordinates: [
              { x: leftX, y: stopY },
              { x: rightX, y: stopY },
            ],
          },
          styles: {
            color: palette.lossLine,
            size: 1.2,
            style: "dashed",
            dashedValue: [6, 4],
          },
        },
        {
          type: "line",
          attrs: {
            coordinates: [
              { x: leftX, y: targetY },
              { x: rightX, y: targetY },
            ],
          },
          styles: {
            color: palette.profitLine,
            size: 1.2,
            style: "dashed",
            dashedValue: [6, 4],
          },
        },
        ...buildStatsFigures(leftX, rightX, topY, bottomY, stats, palette, bounding),
        ...inlinePriceFigures,
      ];
    },
    createYAxisFigures() {
      return [];
    },
    performEventMoveForDrawing({
      currentStep,
      performPointIndex,
      points,
      performPoint,
    }: OverlayPerformEventParams) {
      if (currentStep === 2 && performPointIndex === 1) {
        syncRiskRewardPointsForDrawing(points as Array<Partial<Point>>, performPoint);
      }
    },
    performEventPressedMove(
      this: RiskRewardOverlayState,
      { performPointIndex, points }: OverlayPerformEventParams,
    ) {
      const previousPoints = Array.isArray(this?._riskRewardPrevPressedPoints)
        ? this._riskRewardPrevPressedPoints
        : Array.isArray(this?._prevPressedPoints)
          ? this._prevPressedPoints
          : null;
      updateRiskRewardPointsForDrag(
        points as Array<Partial<Point>>,
        performPointIndex,
        previousPoints,
      );
      this._riskRewardPrevPressedPoints = clonePoints(points as Array<Partial<Point>>);
    },
    onPressedMoveStart({ overlay }: OverlayEvent<unknown>) {
      const typedOverlay = overlay as RiskRewardOverlayState;
      typedOverlay._riskRewardPrevPressedPoints = clonePoints(
        typedOverlay.points as Array<Partial<Point>>,
      );
      typedOverlay._riskRewardDragging = true;
    },
    onPressedMoveEnd({ overlay }: OverlayEvent<unknown>) {
      const typedOverlay = overlay as RiskRewardOverlayState;
      typedOverlay._riskRewardPrevPressedPoints = null;
      typedOverlay._riskRewardDragging = false;
    },
    onMouseEnter({ overlay }: OverlayEvent<unknown>) {
      (overlay as RiskRewardOverlayState)._riskRewardHovered = true;
    },
    onMouseLeave({ overlay }: OverlayEvent<unknown>) {
      (overlay as RiskRewardOverlayState)._riskRewardHovered = false;
    },
    onSelected({ overlay }: OverlayEvent<unknown>) {
      (overlay as RiskRewardOverlayState)._riskRewardSelected = true;
    },
    onDeselected({ overlay }: OverlayEvent<unknown>) {
      const typedOverlay = overlay as RiskRewardOverlayState;
      typedOverlay._riskRewardSelected = false;
      typedOverlay._riskRewardHovered = false;
      typedOverlay._riskRewardDragging = false;
    },
  };
}

export async function registerRiskRewardOverlay() {
  if (registered) {
    return;
  }

  registered = true;
  const { registerOverlay } = await import("klinecharts");
  registerOverlay(createRiskRewardOverlayTemplate());
}
